/**
 * SDK analytics — mirrors the widget's `Analytics.track` pattern.
 *
 * Each event dual-writes to PostHog and Segment when they're available, on top
 * of whatever GA4 / pixel fan-out `ConversionTracking` already does at the
 * callsite. This keeps the SDK consistent with how `apps/embedded-ai-assistant`
 * emits events (see `src/common/lib/analytics.ts` there): explicit per-callsite
 * emission, not implicit fan-out from inside the shared `ConversionTracking`.
 *
 * The SDK is bundle-size-sensitive. `posthog-js` would add ~190 KB raw to the
 * IIFE bundle (IIFE format can't code-split dynamic imports), so we POST to
 * PostHog's `/i/v0/e/` ingest endpoint directly via `navigator.sendBeacon`.
 * We lose autocapture / replay / built-in retry — but the SDK only emits
 * deliberately-authored events, so the tradeoff is correct.
 *
 * Segment gets the same treatment: a private HTTP client (`segment.ts`) that
 * POSTs to Segment's tracking API under Ekho's write key, so every event
 * lands in the SDK's own Segment source. The SDK never reads or writes any
 * host-page analytics global—a dealer's own Segment instance (identity chain,
 * destinations, middleware) is completely untouched.
 *
 * Init is idempotent and skipped entirely when the SDK is config-disabled —
 * `track(...)` is a hard no-op until init runs. Every event carries `ekho_aid`
 * / `ekho_sid` as properties on both destinations so warehouse joins never
 * depend on the destination's own identity state. Failures are logged and
 * swallowed — analytics must never break the SDK.
 */

import { sendsToCdpSink } from '@ekhodealer/ekho-common/analytics';
import { isBotRuntime } from '@ekhodealer/ekho-common/analytics/bot-detection';
import { getEventName } from '@ekhodealer/ekho-common/analytics/events';

import type { EkhoEventDefinition } from '@ekhodealer/ekho-common/analytics/events';
import type { Logger } from '../loader/diagnostics';

import { captureSessionId } from './identity';
import { createSegmentClient, type SegmentClient } from './segment';

interface AnalyticsInitOptions {
  anonymousId: string;
  programOwnerId: string;
  domainOwnerSvpId: string;
  /** SDK BFF base URL, used to build the beacon endpoints. */
  apiBaseUrl: string;
  /** Publishable key, echoed on the beacon so the warehouse can attribute it. */
  key: string;
  log: Logger;
}

interface PostHogCaptureBody {
  ['api_key']: string;
  event: string;
  ['distinct_id']: string;
  properties: Record<string, unknown>;
  timestamp: string;
}

interface PostHogConfig {
  apiKey: string;
  apiHost: string;
}

interface AnalyticsState {
  anonymousId: string;
  /** Tenant context attached to every event, on both destinations. */
  commonProperties: Record<string, unknown>;
  /** PostHog group analytics — PostHog-only, never sent to Segment. */
  groups: Record<string, string>;
  /** Null when the PostHog kill-switch is off or the key is missing. */
  posthog: PostHogConfig | null;
  /** Null when the Segment kill-switch is off or the write key is missing. */
  segment: SegmentClient | null;
  /**
   * Beacon base URL and the identifiers every beacon reports under.
   *
   * The tenant ids are held here, typed, rather than read back out of
   * `commonProperties`, which is a `Record<string, unknown>` and would need a
   * cast at the callsite to recover values this module already knows.
   */
  beacon: {
    baseUrl: string;
    key: string;
    programOwnerId: string;
    domainOwnerSvpId: string;
  };
}

let state: AnalyticsState | null = null;

function resolvePostHogConfig(log: Logger): PostHogConfig | null {
  const apiKey = import.meta.env.VITE_POSTHOG_KEY;
  const enabled = import.meta.env.VITE_POSTHOG_ENABLED === 'true';
  if (!enabled || !apiKey) {
    log('posthog disabled or missing key — skipping posthog capture');
    return null;
  }
  return { apiKey, apiHost: import.meta.env.VITE_POSTHOG_HOST ?? 'https://us.i.posthog.com' };
}

function postToPostHog(config: PostHogConfig, body: PostHogCaptureBody): void {
  const url = `${config.apiHost.replace(/\/$/, '')}/i/v0/e/`;
  const payload = JSON.stringify(body);
  if (typeof navigator.sendBeacon === 'function') {
    const blob = new Blob([payload], { type: 'application/json' });
    if (navigator.sendBeacon(url, blob)) return;
  }
  void fetch(url, {
    method: 'POST',
    body: payload,
    headers: { ['Content-Type']: 'application/json' },
    keepalive: true,
  }).catch(() => {
    // Capture failures are non-fatal — never surface to the host page.
  });
}

/**
 * Initialize analytics with the Ekho-minted anonymous ID. Idempotent. Callers
 * must skip this when the SDK is config-disabled — every `track(...)` before
 * (or without) init is a hard no-op for both destinations.
 *
 * The Segment client anchors identity on the `ekho_aid` cookie itself (read
 * per event), so PostHog and Segment always agree on the anonymous ID.
 */
export function initAnalytics(options: AnalyticsInitOptions): void {
  if (state) return;
  const segment = createSegmentClient({
    writeKey: import.meta.env.VITE_SEGMENT_WRITE_KEY ?? '',
    enabled: import.meta.env.VITE_SEGMENT_ENABLED === 'true',
    log: options.log,
  });
  state = {
    anonymousId: options.anonymousId,
    commonProperties: {
      ekhoApp: 'sdk',
      hostDomain: window.location.hostname,
      programOwnerId: options.programOwnerId,
      domainOwnerSvpId: options.domainOwnerSvpId,
    },
    groups: {
      ['program_owner']: options.programOwnerId,
      ['domain_owner']: options.domainOwnerSvpId,
    },
    posthog: resolvePostHogConfig(options.log),
    segment,
    beacon: {
      baseUrl: options.apiBaseUrl.replace(/\/$/, ''),
      key: options.key,
      programOwnerId: options.programOwnerId,
      domainOwnerSvpId: options.domainOwnerSvpId,
    },
  };
  options.log('analytics initialized', {
    anonymousId: options.anonymousId,
    segmentEnabled: segment !== null,
  });
}

/**
 * Dual-write a custom event to PostHog and Segment, both over direct HTTP.
 * No-op until `initAnalytics` runs; no-op per destination when that
 * destination isn't available.
 *
 * Both destinations get the tenant context (`programOwnerId`,
 * `domainOwnerSvpId`, `ekhoApp`, `hostDomain`) plus `ekho_aid` / `ekho_sid` —
 * matching the server-side `trackSegmentEvent` convention so warehouse joins
 * never depend on the destination's own identity state. Reading the session
 * ID renews its 30-minute sliding window — event activity keeps the session
 * alive.
 *
 * Mirrors the widget's `Analytics.track` pattern — callers explicitly invoke
 * this in addition to whatever `ConversionTracking.trackCustomEvent` does for
 * GA4 / ad pixels at the same callsite.
 *
 * Takes the registry event definition (an `EKHO_EVENTS` entry); the emitted
 * name resolves through `getEventName` (`wireName ?? canonical`).
 */
export function track(event: EkhoEventDefinition, properties: Record<string, unknown> = {}): void {
  if (!state) return;
  const eventName = getEventName(event);
  const enriched = {
    ...state.commonProperties,
    ...properties,
    ['ekho_aid']: state.anonymousId,
    ['ekho_sid']: captureSessionId(),
  };
  if (state.posthog && sendsToCdpSink(event, 'posthog')) {
    postToPostHog(state.posthog, {
      ['api_key']: state.posthog.apiKey,
      event: eventName,
      ['distinct_id']: state.anonymousId,
      properties: { ...enriched, ['$groups']: state.groups },
      timestamp: new Date().toISOString(),
    });
  }
  if (sendsToCdpSink(event, 'segment')) {
    state.segment?.track(eventName, enriched);
  }
}

/**
 * Cap a free-text field before it goes in the query string.
 *
 * Everything the beacon carries rides in the URL, and a page can hold a title
 * or arrive from a referrer of essentially any length. An over-long URL risks
 * the request being rejected or `sendBeacon` refusing it, which would cost the
 * page view itself. A page view with a shortened title is worth far more than
 * no page view, so these two are bounded and the identity fields are not.
 *
 * The limit is generous against real titles and referrers, so in practice this
 * changes nothing; it exists so a pathological page cannot break counting.
 *
 * Truncation counts code points, not the UTF-16 units `length` and `slice`
 * work in. An emoji is two units, so cutting on a unit boundary can leave a
 * lone surrogate, and `encodeURIComponent` throws `URIError` on one of those.
 * That throw happens while the URL is being built, outside the transport's own
 * guard, so it would cost the whole page view: precisely the failure this cap
 * exists to prevent. Counting code points cannot split a pair, and it keeps the
 * boundary character rather than replacing it.
 *
 * The strip afterwards covers a title that arrives malformed on its own, which
 * truncation cannot cause but also cannot repair. With the `u` flag this class
 * matches only an unpaired surrogate, because a well-formed pair is a single
 * code point above U+FFFF and outside the range.
 */
function clampField(value: string): string {
  const points = Array.from(value);
  const clamped = points.length > 300 ? points.slice(0, 300).join('') : value;
  return clamped.replaceAll(/[\uD800-\uDFFF]/gu, '\uFFFD');
}

/**
 * Resolve the automation flag without letting it escape.
 *
 * `isBotRuntime` reads `navigator.userAgentData`, `navigator.userAgent` and
 * `navigator.webdriver`, and guards none of them. A host page can define a
 * throwing getter on any of those, so reading the signal outside a boundary
 * would let a broken or hostile dealer site turn a page-view beacon into a page
 * error, which is the one thing the rest of this path promises never to do.
 *
 * An unresolvable answer reports as non-automated. That matches how the
 * replacement models read a missing flag: keep the row, because dropping a page
 * view that may be real is the worse error.
 */
function resolveAutomationFlag(): '0' | '1' {
  try {
    return isBotRuntime() ? '1' : '0';
  } catch {
    return '0';
  }
}

/** Beacon URL up to and including the identity fields every beacon carries. */
function beaconTarget(current: AnalyticsState, path: '/pv' | '/ev'): string {
  const { baseUrl, key, programOwnerId, domainOwnerSvpId } = current.beacon;
  return (
    `${baseUrl}${path}?key=${encodeURIComponent(key)}` +
    `&programOwnerId=${encodeURIComponent(programOwnerId)}` +
    `&domainOwnerSvpId=${encodeURIComponent(domainOwnerSvpId)}` +
    `&aid=${encodeURIComponent(current.anonymousId)}` +
    `&sid=${encodeURIComponent(captureSessionId())}` +
    `&bot=${resolveAutomationFlag()}`
  );
}

function sendBeaconRequest(target: string): void {
  try {
    if (typeof navigator.sendBeacon === 'function' && navigator.sendBeacon(target)) return;
  } catch {
    // Fall through to fetch: analytics must never break the host page.
  }
  try {
    void fetch(target, {
      method: 'GET',
      mode: 'no-cors',
      cache: 'no-store',
      keepalive: true,
    }).catch(() => {
      // Beacon failures are non-fatal and never surface to the host page.
    });
  } catch {
    // A host page that replaced or removed fetch must not see the beacon throw.
  }
}

/**
 * Report a page view to Ekho's own infrastructure, in place of a metered
 * vendor call.
 *
 * The request is the record. `GET /sdk/pv` answers 204 and stores nothing; the
 * load balancer's own log entry is what a sink routes into BigQuery, where it
 * replaces `sdk_page_view`. That log entry also carries the client's resolved
 * ASN and TLS fingerprint, which the vendor events never did and which are a
 * far stronger automation signal than a user-agent string.
 *
 * `sendBeacon` rather than `fetch`, because a shopper clicking straight
 * through to the next page is the normal case and the browser cancels an
 * in-flight `fetch` on unload. The URL carries everything, so no body is sent,
 * which also keeps this a simple request that never triggers a CORS preflight.
 * The `keepalive` fetch fallback covers browsers where `sendBeacon` is absent
 * or refuses it, which it does by returning false once its queue is over quota.
 *
 * Carries the page title and the referrer because the event it replaces
 * carries them, and around 30 existing queries read them. The referrer is
 * `document.referrer`, which is the same property the vendor event read, so
 * this inherits exactly its blind spots: empty on a direct visit, and subject
 * to the referring site's own policy. Matching the event is the goal, not
 * capturing the truth about where a visitor came from.
 *
 * Reports both tenant ids rather than leaving them to be recovered from the
 * key. The key encodes only the program owner, so the domain owner is not
 * derivable from it: a dealer group serving several rooftops resolves a
 * per-rooftop `domainOwnerSvpId` server-side while every rooftop ships the
 * group's key. Recovering it downstream would mean joining a hostname-to-SVP
 * mapping, which reintroduces a dependency and a staleness mode to rebuild a
 * value the config fetch already resolved authoritatively.
 *
 * Reports automation rather than refusing it, which is the opposite of what
 * the Segment client does with the same signal and is deliberate. Segment
 * prices per tracked user, so a crawler walking a catalogue mints a billable
 * identity per page and is turned away at the client. A beacon costs a log
 * row, so the cheaper thing is to send it and let the warehouse decide: the
 * replacement models exclude `bot=1` to match the population the Segment
 * export holds, while the rows themselves stay queryable for anyone asking how
 * much automation a dealer's site attracts.
 *
 * Sent as an explicit `0` or `1` rather than omitted when false. A loader copy
 * cached on a dealer page before this shipped sends no `bot` at all, so absence
 * has to mean "unknown, from an older build" rather than "human".
 *
 * No-ops until `initAnalytics` runs, which is what makes a config-disabled SDK
 * emit nothing here, exactly as `track(...)` already behaves.
 */
export function trackPageViewBeacon(pageUrl: string, pageTitle: string): void {
  if (!state) return;
  const target =
    beaconTarget(state, '/pv') +
    `&url=${encodeURIComponent(pageUrl)}` +
    // Title and referrer last, so that if anything downstream truncates a long
    // URL the counting fields and the identity survive it.
    `&title=${encodeURIComponent(clampField(pageTitle))}` +
    `&ref=${encodeURIComponent(clampField(document.referrer))}`;
  sendBeaconRequest(target);
}

/** A value the event beacon can carry as one query parameter. */
type EventBeaconValue = string | number | boolean | undefined;

function encodeBeaconValue(value: string | number | boolean): string {
  if (typeof value === 'boolean') return value ? '1' : '0';
  return typeof value === 'string' ? clampField(value) : String(value);
}

/**
 * Record an event at the cost of a load balancer log row instead of a Segment
 * tracked user and a PostHog capture.
 *
 * For events that fire at page-view scale, where either vendor would bill for
 * nearly every visitor. `/sdk/ev` answers 204 and stores nothing, and the same
 * sink that carries `/sdk/pv` routes its log entry into `request_logs`.
 *
 * Carries the page-view beacon's identity fields, then `event` (the wire name)
 * and one parameter per property. Booleans are sent as `1` or `0`, matching
 * `bot`, and an undefined property is left out rather than sent empty. String
 * values are clamped like the page title. The whole URL lands in a log row, so
 * a property must never carry free text, a ZIP, contact details or a URL.
 *
 * Gated exactly like `track(...)`: a no-op until `initAnalytics` runs and after
 * `resetAnalytics`, so consent and the config kill-switch apply unchanged.
 */
export function trackEventBeacon(
  event: EkhoEventDefinition,
  properties: Record<string, EventBeaconValue> = {},
): void {
  if (!state) return;
  let target = beaconTarget(state, '/ev') + `&event=${encodeURIComponent(getEventName(event))}`;
  for (const [name, value] of Object.entries(properties)) {
    if (value === undefined) continue;
    target += `&${encodeURIComponent(name)}=${encodeURIComponent(encodeBeaconValue(value))}`;
  }
  sendBeaconRequest(target);
}

/**
 * Tear down analytics so every later `track(...)` is a no-op. Used on consent
 * withdrawal to stop emission to PostHog / Segment.
 */
export function resetAnalytics(): void {
  state = null;
}

/** Reset internal state. Exported for testing only. */
export function resetAnalyticsForTesting(): void {
  resetAnalytics();
}
